import { Editor } from "@tinymce/tinymce-react";
import { useCallback, useRef, useState } from "react";

// TinyMCE BlobInfo 타입
interface BlobInfo {
  id: () => string;
  name: () => string;
  filename: () => string;
  blob: () => Blob;
  base64: () => string;
  blobUri: () => string;
  uri: () => string | undefined;
}

type ProgressFn = (percent: number) => void;

interface FreedomEditorProps {
  setValue: React.Dispatch<React.SetStateAction<any>>;
  value: string;
  fieldName: string;
  height?: number;
}

// 🔥 이미지 리사이즈 함수 (최대 너비 1300px, 비율 유지)
const resizeImage = (
  file: Blob,
  maxWidth = 1300,
  quality = 0.8
): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // width가 maxWidth보다 클 때만 리사이즈
      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height = height * ratio; // 🔥 비율 유지
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, width, height);

      const resizedBase64 = canvas.toDataURL("image/jpeg", quality);
      resolve(resizedBase64);
    };

    reader.readAsDataURL(file);
  });
};

export const FreedomEditor = ({
  setValue,
  value,
  fieldName,
  height = 500,
}: FreedomEditorProps) => {
  const editorRef = useRef<any>(null);
  const [images, setImages] = useState<string[]>([]);

  // 🔥 TinyMCE 업로드 핸들러(드래그&드롭 / 붙여넣기 / 기본 업로드 모두 처리)
  const handleImageUpload = async (
    blobInfo: BlobInfo,
    progress?: ProgressFn
  ): Promise<string> => {
    try {
      const originalBlob = blobInfo.blob();

      // 🔥 리사이즈 적용
      const resizedBase64 = await resizeImage(originalBlob);

      setImages((prev) => [...prev, resizedBase64]);

      if (progress) progress(100);

      return resizedBase64;
    } catch (error) {
      console.error("이미지 처리 오류:", error);
      throw error;
    }
  };

  // 에디터 내용 변경
  const handleEditorChange = useCallback(
    (content: string) => {
      setValue((prev: any) => ({
        ...prev,
        [fieldName]: content,
        images: images,
      }));
    },
    [setValue, fieldName, images]
  );

  return (
    <div className="freedom-editor-container">
      <Editor
        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
        onInit={(_evt, editor) => (editorRef.current = editor)}
        value={value ?? ""}
        onEditorChange={handleEditorChange}
        init={{
          height,
          menubar: true,
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "preview",
            "anchor",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "help",
            "wordcount",
          ],
          toolbar:
            "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image | removeformat | help",

          images_upload_handler: handleImageUpload as any,
          paste_data_images: true,
          automatic_uploads: true,
          file_picker_types: "image",

          // 🔥 파일 선택 업로더에서도 리사이즈 적용
          file_picker_callback: function (callback, value, meta) {
            if (meta.filetype === "image") {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";

              input.onchange = async function () {
                if (input.files && input.files[0]) {
                  const resizedBase64 = await resizeImage(input.files[0]);

                  callback(resizedBase64, { alt: "업로드 이미지" });
                  setImages((prev) => [...prev, resizedBase64]);
                }
              };

              input.click();
            }
          },

          content_style:
            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
          promotion: false,
          statusbar: false,
          branding: false,
        }}
      />
    </div>
  );
};
